import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { NavBar } from 'components/NavBar/NavBar'
import { NoData } from 'components/NoData'
import { Flex, PageWrapper } from 'styling/GlobalStyles'
import { OtherText } from 'styling/Typography'
import { RootState } from 'store'
import { IOpsTodayQuickLink } from 'types/opsToday.interface'
import { useOpsBootstrap } from 'hooks/useOpsBootstrap'
import { USE_OPS_MOCKS } from '../Shared/opsControl'
import { DemoBadge } from '../Shared/OpsSharedComponents'
import { EnvironStrip } from './EnvironStrip'
import { TodayQuestionCard } from './TodayQuestionCard'
import { TodayInbox } from './TodayInbox'
import { TodaySwitcher } from './TodaySwitcher'
import { SiteRequestsSummary } from './TodayRequests'
import { buildInboxItems, buildSiteCards } from './todayData'
import { FooterChip, FooterRow, QuestionsGrid, TodayTitle } from './OpsToday.styled'

const QUICK_LINKS: IOpsTodayQuickLink[] = [
  { label: 'Full timeline', path: '/operations/timeline' },
  { label: 'My departments', path: '/operations/department' },
  { label: 'Handover report', path: '/operations/handover' },
  { label: 'Overview', path: '/operations/overview' },
]

const ACTIVE_STATUSES = ['open', 'acknowledged', 'in_progress']

export const OpsToday = () => {
  const navigate = useNavigate()
  useOpsBootstrap()
  const environ = useSelector((s: RootState) => s.opsEnviron.environ)
  const requests = useSelector((s: RootState) => s.opsRequests.requests)
  const environLoading = useSelector((s: RootState) => s.opsEnviron.isLoading)
  const requestsLoading = useSelector((s: RootState) => s.opsRequests.isLoading)

  const activeRequests = useMemo(
    () => requests.filter((r) => ACTIVE_STATUSES.includes(r.status)),
    [requests],
  )

  const cards = USE_OPS_MOCKS ? buildSiteCards() : []
  const hasContent = Boolean(environ) || activeRequests.length > 0 || cards.length > 0
  const bootstrapDone = !environLoading && !requestsLoading

  return (
    <>
      <NavBar />
      <PageWrapper>
        <Flex center gap='0.75rem'>
          <TodayTitle>Today</TodayTitle>
          <DemoBadge />
        </Flex>
        <OtherText grey>Day Shift · 06:00–18:00</OtherText>
        <TodaySwitcher activeKey='site' />
        {hasContent ? (
          <>
            <EnvironStrip />
            {cards.length > 0 && (
              <QuestionsGrid>
                {cards.map((card) => (
                  <TodayQuestionCard key={card.id} card={card} />
                ))}
              </QuestionsGrid>
            )}
            {USE_OPS_MOCKS && <TodayInbox initialItems={buildInboxItems()} />}
            <SiteRequestsSummary />
          </>
        ) : bootstrapDone ? (
          <NoData text='Nothing scheduled for today yet. Raise a cross-department request or check the timeline for site activity.' />
        ) : null}
        <FooterRow>
          {QUICK_LINKS.map((link) => (
            <FooterChip key={link.path} onClick={() => navigate(link.path)}>
              {link.label}
            </FooterChip>
          ))}
        </FooterRow>
      </PageWrapper>
    </>
  )
}
